import type { ReactNode } from 'react';

export type FormMessageTone = 'error' | 'hint' | 'success';

export interface FormMessageProps {
  uiId: string;
  tone: FormMessageTone;
  children: ReactNode;
}

/**
 * 各 tone 的文字顏色（§7 第 3 級證據，【雛形值待實機校準】）：
 *   error   `.help-block { color:#a94442 }`（bootstrap.css:2910-2915）── INSPINIA 沒有覆寫，
 *           所以實際渲染出來就是這個 bootstrap 的暗紅，不是 --color-danger (#ed5565)。
 *           theme.css 沒有這個 token，用 arbitrary value 並註明出處（§8.1）。
 *   success `.has-success .control-label { color:#637d4c }`（css/style.css:1839-1841）→ --color-brand
 *   hint    原系統沒有對應 class（提示文字多半直接寫 `<span class="text-muted">`），
 *           雛形用 --color-ink-muted (#999999)，這一項證據最弱。
 */
const TONE_CLASS: Record<FormMessageTone, string> = {
  error: 'text-[#a94442]',
  hint: 'text-ink-muted',
  success: 'text-brand',
};

/** error 才要被螢幕閱讀器主動朗讀，hint／success 不搶焦點。 */
const TONE_ROLE: Record<FormMessageTone, 'alert' | undefined> = {
  error: 'alert',
  hint: undefined,
  success: undefined,
};

/**
 * 欄位下方的訊息（錯誤／提示／成功）。
 *
 * `.help-block { display:block; margin-top:5px; margin-bottom:10px }`（bootstrap.css:2910-2913）。
 *
 * ponytail: 這裡只負責「顯示一段訊息」。它把自己的 uiId 同時當成 DOM id 渲染出來，
 * 讓上層可以拿去做 aria-describedby；但實際把欄位和訊息綁起來是第二層 FormGrid 的事，
 * 因為只有它同時看得到欄位與訊息兩邊的 ID。
 */
export function FormMessage({ uiId, tone, children }: FormMessageProps) {
  return (
    <p
      id={uiId}
      data-ui-id={uiId}
      role={TONE_ROLE[tone]}
      className={`mt-[5px] mb-[10px] block text-sm ${TONE_CLASS[tone]}`}
    >
      {children}
    </p>
  );
}
